import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Warehouse, Plus, Edit2, Trash2, Star, CheckCircle2, XCircle, X as XIcon, Ban } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { useAuth, cn } from '@/lib/auth'

interface Deposito {
  id: number
  nome: string
  codigo: string
  tipo: string
  is_default: boolean
  permite_venda: boolean
  ativo: boolean
  observacao: string | null
  filial_id: number | null
}

const TIPOS = [
  { value: 'principal', label: 'Principal', cor: 'bg-brand-100 text-brand-800' },
  { value: 'loja',      label: 'Loja',      cor: 'bg-emerald-100 text-emerald-800' },
  { value: 'avarias',   label: 'Avarias',   cor: 'bg-red-100 text-red-700' },
  { value: 'transito',  label: 'Trânsito',  cor: 'bg-amber-100 text-amber-800' },
  { value: 'outros',    label: 'Outros',    cor: 'bg-gray-100 text-gray-700' },
]

const TIPO_LABEL: Record<string, { label: string; cor: string }> = Object.fromEntries(
  TIPOS.map(t => [t.value, { label: t.label, cor: t.cor }])
)

export default function DepositosPage() {
  const { user: current } = useAuth()
  const [depositos, setDepositos] = useState<Deposito[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<Deposito | null>(null)

  const canManage = current?.perfil === 'admin' || current?.perfil === 'super_admin'

  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-dep-list'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const filialNome = (id: number | null) => {
    if (id == null) return null
    const f = filiais.find((x: any) => x.id === id)
    return f ? (f.nome_fantasia || f.codigo || `Filial ${id}`) : `Filial ${id}`
  }

  const carregar = async () => {
    setLoading(true)
    try {
      // Tela de gestão (admin): mostra TODOS os depósitos, independente da filial
      // selecionada no topo — senão depósitos de outra loja "somem" da lista.
      const { data } = await api.get('/estoque/depositos', { params: { todas_filiais: true } })
      setDepositos(data)
    } catch {} finally {
      setLoading(false)
    }
  }

  useEffect(() => { carregar() }, [])

  const abrirNovo = () => { setEditando(null); setModalOpen(true) }
  const abrirEditar = (d: Deposito) => { setEditando(d); setModalOpen(true) }

  const deletar = async (d: Deposito) => {
    if (d.is_default) { toast.error('Não é possível deletar o depósito default'); return }
    try {
      await api.delete(`/estoque/depositos/${d.id}`)
      toast.success('Depósito desativado')
      carregar()
    } catch {}
  }

  if (!canManage) {
    return (
      <div className="p-8 max-w-xl">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
          Apenas administradores podem gerenciar depósitos.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Warehouse size={22} /> Depósitos
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Locais físicos de estoque da empresa. O depósito default é usado por integrações (Marketplace, Tray).
          </p>
        </div>
        <button
          onClick={abrirNovo}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700"
        >
          <Plus size={16} /> Novo depósito
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Nome</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Código</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Tipo</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Filial</th>
              <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Default</th>
              <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Venda</th>
              <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Ativo</th>
              <th className="text-right text-xs font-semibold text-gray-600 uppercase px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400 text-sm">Carregando...</td></tr>
            )}
            {!loading && depositos.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400 text-sm">Nenhum depósito</td></tr>
            )}
            {!loading && depositos.map(d => {
              const tipoInfo = TIPO_LABEL[d.tipo] || { label: d.tipo, cor: 'bg-gray-100 text-gray-700' }
              return (
                <tr key={d.id}
                  onClick={() => abrirEditar(d)}
                  className={cn('hover:bg-violet-50 cursor-pointer', !d.ativo && 'opacity-50 bg-gray-50')}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{d.nome}</span>
                      {d.is_default && <Star size={13} className="text-amber-500 fill-amber-500" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{d.codigo}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex text-[11px] font-semibold uppercase px-2 py-0.5 rounded', tipoInfo.cor)}>
                      {tipoInfo.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {filialNome(d.filial_id)
                      ? <span className="text-gray-700">{filialNome(d.filial_id)}</span>
                      : <span className="text-gray-400 text-xs">compartilhado</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {d.is_default
                      ? <CheckCircle2 size={16} className="text-emerald-600 inline" />
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {d.permite_venda
                      ? <CheckCircle2 size={16} className="text-emerald-600 inline" />
                      : <Ban size={16} className="text-red-500 inline" />}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {d.ativo
                      ? <CheckCircle2 size={16} className="text-emerald-600 inline" />
                      : <XCircle size={16} className="text-gray-400 inline" />}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => abrirEditar(d)} title="Editar"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-brand-600">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => pedirExclusao({ descricao: `o depósito "${d.nome}"`, confirmLabel: 'Desativar', onConfirm: () => deletar(d) })}
                        title={d.is_default ? 'Não é possível deletar o default' : 'Desativar'}
                        disabled={d.is_default}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        <strong>Dica:</strong> Integrações (Mercado Livre, Tray, e-commerce) sempre usam o depósito marcado como <em>Default</em>.
        Vendas digitadas e PDV podem escolher qualquer depósito que tenha <em>venda habilitada</em>.
      </div>

      <DepositoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        deposito={editando}
        onSaved={carregar}
      />
    </div>
  )
}


interface ModalProps {
  open: boolean
  onClose: () => void
  deposito: Deposito | null
  onSaved: () => void
}

function DepositoModal({ open, onClose, deposito, onSaved }: ModalProps) {
  const [nome, setNome] = useState('')
  const [codigo, setCodigo] = useState('')
  const [tipo, setTipo] = useState('outros')
  const [isDefault, setIsDefault] = useState(false)
  const [permiteVenda, setPermiteVenda] = useState(true)
  const [ativo, setAtivo] = useState(true)
  const [observacao, setObservacao] = useState('')
  const [filialId, setFilialId] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-dep-form'],
    queryFn: async () => (await api.get('/filiais')).data,
  })

  useEffect(() => {
    if (open) {
      setNome(deposito?.nome || '')
      setCodigo(deposito?.codigo || '')
      setTipo(deposito?.tipo || 'outros')
      setIsDefault(deposito?.is_default || false)
      setPermiteVenda(deposito?.permite_venda ?? true)
      setAtivo(deposito?.ativo ?? true)
      setObservacao(deposito?.observacao || '')
      setFilialId(deposito?.filial_id != null ? String(deposito.filial_id) : '')
      setLoading(false)
    }
  }, [open, deposito])

  if (!open) return null

  const isEdicao = !!deposito

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const payload = {
        nome, codigo: codigo.toUpperCase(), tipo,
        is_default: isDefault, permite_venda: permiteVenda,
        ativo, observacao: observacao || null,
        filial_id: filialId ? Number(filialId) : null,
      }
      if (isEdicao && deposito) {
        await api.put(`/estoque/depositos/${deposito.id}`, payload)
        toast.success('Depósito atualizado')
      } else {
        await api.post('/estoque/depositos', payload)
        toast.success('Depósito criado')
      }
      onSaved()
      onClose()
    } catch {
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-900">
            {isEdicao ? 'Editar depósito' : 'Novo depósito'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <XIcon size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input type="text" required autoFocus value={nome} onChange={e => setNome(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
                placeholder="Depósito Central" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
              <input type="text" required maxLength={20} value={codigo}
                onChange={e => setCodigo(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm font-mono uppercase"
                placeholder="DEP01" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select value={tipo} onChange={e => setTipo(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm bg-white">
                {TIPOS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filial {filiais.length === 0 && <span className="text-xs text-amber-600">(cadastre em Config. → Filiais)</span>}
              </label>
              <select value={filialId} onChange={e => setFilialId(e.target.value)}
                disabled={filiais.length === 0}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400">
                <option value="">— Empresa toda (compartilhado) —</option>
                {filiais.map((f: any) => (
                  <option key={f.id} value={f.id}>{f.codigo} — {f.nome_fantasia}{f.is_matriz ? ' (Matriz)' : ''}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}
                className="w-4 h-4 text-brand-600 rounded" />
              <span><strong>Default</strong> — usado pelas integrações (ML, Tray, e-commerce)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={permiteVenda} onChange={e => setPermiteVenda(e.target.checked)}
                className="w-4 h-4 text-brand-600 rounded" />
              <span><strong>Permite venda</strong> — aparece no seletor de Novo Pedido e PDV</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)}
                className="w-4 h-4 text-brand-600 rounded" />
              <span><strong>Ativo</strong></span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
            <textarea rows={2} value={observacao} onChange={e => setObservacao(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="Opcional" />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
              {loading ? 'Salvando...' : (isEdicao ? 'Salvar' : 'Criar depósito')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
